import { validateFormFields } from '@theme/form-validation';
import { translateSubtree } from '@theme/utilities';

const selectors = {
  FIELD_INPUT: '[data-selector="field-input"]',
  FIELD_ERROR: '[data-selector="field-error"]',
  BUTTON_SUBMIT: '[data-selector="button-submit"]',
  ACCEPT_MARKETING: '[data-selector="accepts-marketing"]',
  ACCEPT_SMS: '[data-selector="accepts-sms"]',
  JOIN_REWARDS: '[data-selector="join-rewards"]',
  SUCCESS_FORM: '[data-selector="form-success"]',
  FORM_FIELDS: '[data-selector="form-fields"]',
  MESSAGE_ERROR: '[data-selector="message-error"]',
  MESSAGE_ACCOUNT_EXISTS: '[data-selector="message-account-exists"]',
  CONTENT_MESSAGE_ERROR: '.core-toast__message',
};

const classes = {
  INPUT_ERROR: 'input-error',
  BUTTON_LOADING: 'button--loading',
  HIDDEN: 'hidden',
};

class SignupForm extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    this.apiEndpoint = this.dataset?.endpoint || null;
    this.fieldInputEls = /** @type {NodeListOf<HTMLInputElement>} */ (
      this.querySelectorAll(selectors.FIELD_INPUT)
    );
    this.acceptMarketingEl = /** @type {HTMLInputElement} */ (
      this.querySelector(selectors.ACCEPT_MARKETING)
    );
    this.acceptSmsEl = /** @type {HTMLInputElement} */ (this.querySelector(selectors.ACCEPT_SMS));
    this.joinRewardsEl = /** @type {HTMLInputElement} */ (
      this.querySelector(selectors.JOIN_REWARDS)
    );
    this.submitButton = this.querySelector(selectors.BUTTON_SUBMIT);
    this.successFormEl = this.querySelector(selectors.SUCCESS_FORM);
    this.formFieldsEl = this.querySelector(selectors.FORM_FIELDS);
    this.messageErrorEl = this.querySelector(selectors.MESSAGE_ERROR);
    this.messageAccountExistsEl = this.querySelector(selectors.MESSAGE_ACCOUNT_EXISTS);
    this.contentMessageErrorEl = this.messageErrorEl?.querySelector(
      selectors.CONTENT_MESSAGE_ERROR,
    );

    this.#attachListeners();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;
  }

  #attachListeners = () => {
    if (!this.apiEndpoint) {
      console.warn('Missing API endpoint', { apiEndpoint: this.apiEndpoint });
      return;
    }

    this.submitButton?.addEventListener('click', this.#handleSubmit, {
      signal: this.abortController?.signal,
    });

    this.fieldInputEls?.forEach((input) => {
      input.addEventListener('input', this.#handleInput, { signal: this.abortController?.signal });
    });
  };

  #handleSubmit = async () => {
    const { isValid, values, firstInvalidEl } = validateFormFields(this.fieldInputEls);
    let url = this.apiEndpoint;

    if (!isValid) {
      // focus() scrolls by default, which fights the smooth scroll below
      firstInvalidEl?.focus({ preventScroll: true });
      firstInvalidEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    if (Object.keys(values).length === 0 || !url) return;

    this.messageAccountExistsEl?.classList.add(classes.HIDDEN);
    this.messageErrorEl?.classList.add(classes.HIDDEN);

    if (this.submitButton) {
      this.submitButton.classList.add(classes.BUTTON_LOADING);
      // disabled is the double-submit guard — a disabled button fires no click
      this.submitButton.toggleAttribute('disabled', true);
      this.submitButton.setAttribute('aria-busy', 'true');
    }

    if (this.joinRewardsEl) {
      values.join_rewards = this.joinRewardsEl.checked;
    }

    url += `${url.includes('?') ? '&' : '?'}market=${window.Shopify.country}`;

    const body = {
      ...values,
      marketing: {
        is_email: this.acceptMarketingEl?.checked || false,
        is_sms: this.acceptSmsEl?.checked || false,
      },
    };

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: this.abortController?.signal,
      });

      if (!response.ok) {
        this.#renderErrorMessage({ action: null, message: null });
        throw new Error(`Status: ${response.status} - Message: ${response.statusText}`);
      }

      const data = await response.json();

      console.log('data', data, data.success);

      if (!data.success) {
        this.#renderErrorMessage(data);
      } else {
        this.#renderSuccessMessage();
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error(error);
    } finally {
      this.submitButton?.classList.remove(classes.BUTTON_LOADING);
      this.submitButton?.toggleAttribute('disabled', false);
      this.submitButton?.removeAttribute('aria-busy');
    }
  };

  #handleInput = (/** @type {Event} */ event) => {
    const inputEl = /** @type {HTMLInputElement} */ (event.currentTarget);
    const fieldErrorEl = inputEl.closest('.core-input')?.querySelector(selectors.FIELD_ERROR);

    inputEl.classList.remove(classes.INPUT_ERROR);
    inputEl.removeAttribute('aria-invalid');
    inputEl.removeAttribute('aria-describedby');

    if (!fieldErrorEl) return;

    fieldErrorEl.textContent = '';
    fieldErrorEl.toggleAttribute('hidden', true);
  };

  #renderSuccessMessage = () => {
    this.successFormEl?.classList.remove(classes.HIDDEN);
    this.formFieldsEl?.classList.add(classes.HIDDEN);
  };

  #renderErrorMessage = (/** @type {{ action: string | null, message: string | null }} */ data) => {
    switch (data.action) {
      case 'IgnoreLoyaltyEnrolledCustomerA':
      case 'IgnoreLoyaltySignedUpCustomer':
      case 'IgnoreCustomerFoundNoLoyalty':
      case 'IgnoreLoyaltyEnrolledCustomer':
        this.messageAccountExistsEl?.classList.remove(classes.HIDDEN);
        break;

      default: {
        this.messageErrorEl?.classList.remove(classes.HIDDEN);
        if (!this.contentMessageErrorEl) break;

        this.contentMessageErrorEl.textContent =
          data.message || window.THEME_TRANSLATIONS.signup_form.something_went_wrong;
        translateSubtree(this.contentMessageErrorEl);
        break;
      }
    }
  };
}

if (!customElements.get('signup-form')) {
  customElements.define('signup-form', SignupForm);
}
